import { isWhiteboard } from './whiteboard.js';

/**
 * The de-dupe latch that decides whether to start fetching the presenter's
 * document.
 *
 * A follower must not fire the same request twice, so the id being fetched is
 * held in a ref and re-requests for it are refused. The subtlety is what
 * happens when an attempt is CANCELLED rather than finished: the latch has to
 * be released either way, or that document can never be requested again for
 * the life of the mount.
 *
 * That is not a hypothetical. `presenterDocId` blinks to null every few
 * seconds on its own -- the presenter holds a second Live Share connection
 * (their meeting stage) that never broadcasts, so its placeholder periodically
 * answers for them and resolvePresenterView correctly rejects it. Each blink
 * re-runs the effect and cancels whatever fetch is in flight.
 *
 * On a desktop the document arrives well inside that window and nothing shows.
 * On a phone the fetch carries the whole file as base64 over a slower link and
 * routinely does not, so the cancel lands mid-flight, the latch is never
 * released, and the follower sits on an empty stage while the presenter is
 * plainly sharing. Re-adding the app "fixes" it because a fresh mount gets a
 * fresh ref -- which is exactly what made this look like a Live Share problem
 * for three sessions rather than a leaked latch. Reported 2026-09-02,
 * diagnosed 2026-09-04 from a handset readout showing presenterDoc alternating
 * with null every ~4s while following was healthy.
 */

/** Whether to begin fetching `wanted`, given what is already in flight. */
export function shouldBeginFetch(
  inFlight: string | null,
  wanted: string | null,
  openDocumentId: string | null | undefined,
  isFollowing: boolean,
): boolean {
  if (!isFollowing) return false;
  if (!wanted) return false;
  // The presenter is on the whiteboard. There is nothing to fetch -- it is
  // rendered, never stored -- and asking /api/documents for it can only fail.
  if (isWhiteboard(wanted)) return false;
  // Already showing it — following has nothing to do.
  if (wanted === openDocumentId) return false;
  // Already asking for it.
  if (wanted === inFlight) return false;
  return true;
}

/**
 * The latch after an attempt for `id` ends, whether it completed or was
 * cancelled.
 *
 * Only clears the latch if `id` is still the one it holds. A cancelled attempt
 * settles AFTER its replacement has already claimed the latch, so clearing
 * unconditionally would unlatch the new in-flight request and let a third be
 * fired for the same document.
 */
export function releaseFetch(inFlight: string | null, id: string): string | null {
  return inFlight === id ? null : inFlight;
}
